import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useToastContext } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import {
 useAvailablePlans,
 subscribe,
 useVendorSubscriptionStatus,
 invalidateVendorSubscriptionStatus,
} from '../../services/subscriptionService';
import PaymentModal from '../../components/shared/PaymentModal';
import {
 normalizeBillingChoice,
 planIsFree,
 planMaxTierPrice,
 planMonthlyPrice,
 planOffersBothIntervals,
 planYearlyPrice,
 priceForBillingChoice,
 sortPlansForDisplay,
 resolvePopularPaidPlanSlug,
 planTierLabel,
 planTierSubtitle,
} from '../../utils/subscriptionPlanPricing';
import {
 Check,
 X,
 Calendar,
 Users,
 Image,
 BarChart3,
 Palette,
 Headphones,
 Code,
 Clock,
 MapPin,
 Star,
 AlertCircle,
 ArrowLeft,
 Sparkles,
 Package,
 ListChecks,
 Crown,
} from 'lucide-react';
import { VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorSubscription = () => {
 const queryClient = useQueryClient();
 const { user } = useAuth();
 const { success, error: showError } = useToastContext();
 const { checkSubscriptionStatus } = useSubscription();
 const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
 const navigate = useNavigate();

 const [selectedPlan, setSelectedPlan] = useState(null);
 const [showPaymentModal, setShowPaymentModal] = useState(false);
 const [subscribingToFree, setSubscribingToFree] = useState(false);
 const [globalBilling, setGlobalBilling] = useState('monthly');

 const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } =
 useAvailablePlans();

 const subscriptions = useMemo(() => subscriptionsData?.data || [], [subscriptionsData?.data]);

 const sortedPlans = useMemo(() => sortPlansForDisplay(subscriptions), [subscriptions]);
 const popularPaidSlug = useMemo(() => resolvePopularPaidPlanSlug(sortedPlans), [sortedPlans]);
 const showGlobalBillingToggle = useMemo(
 () => sortedPlans.some((p) => planOffersBothIntervals(p)),
 [sortedPlans]
 );

 const { data: vendorStatusResponse, refetch: refetchVendorStatus } = useVendorSubscriptionStatus({
 enabled: user?.user_type === 'vendor',
 });
 const vendorSubPayload = vendorStatusResponse?.data ?? vendorStatusResponse;

 const currentPlan = useMemo(() => {
 const id = vendorSubPayload?.current_subscription?.subscription_plan_id;
 if (!id || !subscriptions.length) return null;
 return subscriptions.find((p) => p.id === id) || null;
 }, [vendorSubPayload, subscriptions]);

 const isFreeTierActive = Boolean(vendorSubPayload?.current_subscription?.is_free_tier);
 const hasUsedFreeTier = Boolean(vendorSubPayload?.has_used_free_tier);
 const canSelectFreeTier =
 vendorSubPayload == null ? false : Boolean(vendorSubPayload.can_select_free_tier);

 const visiblePlans = useMemo(() => {
 return sortedPlans.filter((plan) => {
 if (!planIsFree(plan)) return true;
 if (isFreeTierActive) return true;
 return canSelectFreeTier;
 });
 }, [sortedPlans, isFreeTierActive, canSelectFreeTier]);

 const gridColsClass = useMemo(() => {
 const n = visiblePlans.length;
 if (n >= 4) return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4';
 if (n === 3) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
 return 'grid-cols-1 md:grid-cols-2';
 }, [visiblePlans.length]);

 const comparePlanTier = (current, plan) => {
 const so = Number(plan.sort_order) - Number(current.sort_order);
 if (so !== 0) return so;
 return planMaxTierPrice(plan) - planMaxTierPrice(current);
 };

 const canStillUpgrade = useMemo(() => {
 if (!currentPlan || !subscriptions.length) return true;
 const maxSort = Math.max(...subscriptions.map((p) => Number(p.sort_order ?? 0)));
 const isFree = planIsFree(currentPlan);
 return isFree || Number(currentPlan.sort_order) < maxSort;
 }, [currentPlan, subscriptions]);

 useEffect(() => {
 if (user?.user_type !== 'vendor') {
 navigate('/');
 return;
 }
 if (isChecking || subscriptionsLoading || hasActiveSubscription === null) return;
 if (hasActiveSubscription !== true || !subscriptions.length) return;

 const timer = setTimeout(async () => {
 try {
 const response = await refetchVendorStatus();
 const sub = response?.data?.data?.current_subscription;
 if (!sub) return;
 const current = subscriptions.find((p) => p.id === sub.subscription_plan_id);
 if (!current) return;

 const maxSort = Math.max(...subscriptions.map((p) => Number(p.sort_order ?? 0)));
 const isFree = planIsFree(current);
 const canUpgrade = isFree || Number(current.sort_order) < maxSort;

 if (canUpgrade) return;

 const daysRemaining = sub.days_remaining;
 if (daysRemaining != null && daysRemaining <= 5) return;

 navigate('/vendor', { replace: true });
 } catch (e) {
 console.error(e);
 }
 }, 400);
 return () => clearTimeout(timer);
 }, [
 user?.user_type,
 hasActiveSubscription,
 isChecking,
 subscriptionsLoading,
 subscriptions,
 navigate,
 refetchVendorStatus,
 ]);

 const handleSelectPlan = async (plan) => {
 if (currentPlan) {
 if (plan.id === currentPlan.id) {
 showError('Current plan', 'You are already on this plan.');
 return;
 }
 if (comparePlanTier(currentPlan, plan) < 0) {
 showError(
 'Lower plan',
 'Downgrading is not available here. Contact support if you need to change to a lower tier.'
 );
 return;
 }
 }

 const isFree = planIsFree(plan);
 if (isFree) {
 if (hasUsedFreeTier && !isFreeTierActive) {
 showError('Free plan unavailable', 'The free plan is available only once. Please choose a paid plan.');
 return;
 }
 try {
 setSubscribingToFree(true);
 const response = await subscribe({
 subscription_id: plan.id,
 payment_method: 'free',
 billing_interval: 'monthly',
 });
 if (response.success) {
 handlePaymentSuccess(response);
 } else {
 showError(response.message || 'Failed to subscribe to free plan');
 }
 } catch (err) {
 showError(err?.response?.data?.message || err?.message || 'Failed to subscribe to free plan');
 } finally {
 setSubscribingToFree(false);
 }
 } else {
 setSelectedPlan(plan);
 setShowPaymentModal(true);
 }
 };

 const handlePaymentSuccess = (data) => {
 const msg =
 data?.message ||
 (data?.subscription ? 'Your plan has been updated.' : 'Successfully subscribed! Welcome to your new plan.');
 success('Subscription', msg);

 checkSubscriptionStatus();
 invalidateVendorSubscriptionStatus(queryClient);

 setShowPaymentModal(false);

 setTimeout(() => {
 navigate('/vendor', { replace: true });
 }, 1000);
 };

 const handlePaymentError = (error) => {
 showError(error || 'Payment failed. Please try again.');
 };

 const getFeatureIcon = (feature) => {
 const icons = {
 analytics: BarChart3,
 custom_branding: Palette,
 priority_support: Headphones,
 api_access: Code,
 advanced_booking: Clock,
 multi_location: MapPin,
 };
 return icons[feature] || Check;
 };

 const renderFeature = (feature, value, label) => {
 const Icon = getFeatureIcon(feature);
 return (
 <div className="flex items-center gap-2.5">
 <span
 className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${ value ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400' }`}
 >
 {value ? <Check className="h-3 w-3" strokeWidth={2.5} /> : <X className="h-3 w-3" />}
 </span>
 <Icon className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 <span
 className={`text-sm ${ value ? 'text-ink-secondary' : 'text-ink-tertiary' }`}
 >
 {label}
 </span>
 </div>
 );
 };

 const planCardShell = (plan) => {
 const isPopular = plan.slug === popularPaidSlug && !planIsFree(plan);
 const isCurrent = currentPlan && plan.id === currentPlan.id;

 if (planIsFree(plan)) {
 return `relative flex h-full flex-col overflow-hidden rounded-lg border border-dashed border-border bg-surface-muted ${
 isCurrent ? 'ring-2 ring-gray-400/40' : ''
 }`;
 }
 if (isPopular) {
 return 'relative flex h-full flex-col overflow-hidden rounded-lg border-2 border-ink bg-surface';
 }
 return `relative flex h-full flex-col overflow-hidden product-panel shadow-sm hover:shadow-md hover:border-ink transition-all duration-200 ${
 isCurrent ? 'ring-2 ring-ink/20' : ''
 }`;
 };

 const planButtonClass = (plan) => {
 if (currentPlan && plan.id === currentPlan.id) {
 return 'bg-surface-muted text-ink-tertiary cursor-not-allowed border border-transparent';
 }
 if (currentPlan && comparePlanTier(currentPlan, plan) < 0) {
 return 'cursor-not-allowed border border-transparent bg-surface-muted text-ink-tertiary';
 }
 if (planIsFree(plan)) {
 return 'border border-ink bg-surface text-ink hover:bg-surface-muted';
 }
 if (plan.slug === popularPaidSlug) {
 return 'bg-ink text-white hover:bg-black border border-transparent';
 }
 return 'bg-ink text-white hover:bg-black border border-transparent';
 };

 useEffect(() => {
 if (subscriptionsError) {
 showError(subscriptionsError.message || 'Failed to load subscription plans');
 }
 }, [subscriptionsError, showError]);

 if (isChecking || subscriptionsLoading) {
 return (
 <div className="min-h-screen bg-canvas flex items-center justify-center">
 <div className="text-center px-4">
 <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center product-panel shadow-sm">
 <div className="h-5 w-5 animate-spin rounded-full border-2 border-ink border-t-transparent" />
 </div>
 <p className="text-sm font-medium text-ink">
 {isChecking ? 'Checking your plan…' : 'Loading plans…'}
 </p>
 <p className="mt-1 text-xs text-ink-tertiary">This only takes a moment</p>
 </div>
 </div>
 );
 }

 const pageTitle =
 hasActiveSubscription && canStillUpgrade ? 'Upgrade your subscription' : 'Choose your plan';
 const pageSubtitle =
 hasActiveSubscription && canStillUpgrade
 ? 'Each tier is built for a different stage — compare limits and premium tools, then pick what fits.'
 : hasUsedFreeTier && !isFreeTierActive
 ? 'Your free starter period has ended. Choose a paid plan below to keep using the platform.'
 : 'Compare tiers below. New vendors start on Free with core access; upgrade when you need more.';

 return (
 <div className="relative min-h-screen overflow-hidden bg-canvas">
 <div
 className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-surface-muted via-transparent to-transparent"
 aria-hidden
 />

 <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
 <div className="mb-8 sm:mb-10">
 <button
 type="button"
 onClick={() => navigate('/vendor')}
 className="inline-flex items-center gap-2 rounded-product-md border border-border bg-surface px-3.5 py-1.5 text-xs font-medium text-ink-secondary transition-colors hover:border-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="h-4 w-4" />
 Back to dashboard
 </button>

 <div className="mt-8 max-w-2xl">
 <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-tertiary">
 <Crown className="h-3.5 w-3.5 text-ink" />
 Plans &amp; billing
 </div>
 <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink">
 {pageTitle}
 </h1>
 <p className="mt-3 text-base sm:text-lg text-ink-secondary leading-relaxed">
 {pageSubtitle}
 </p>
 </div>
 </div>

 <div className="space-y-5 mb-8 sm:mb-10 max-w-3xl">
 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.subscription.title}
 steps={VENDOR_PAGE_GUIDES.subscription.steps}
 icon={ListChecks}
 className="text-left"
 />

 {hasActiveSubscription && currentPlan && (
 <div
 className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 sm:px-5 ${ isFreeTierActive ? 'border-amber-200/90 bg-amber-50/90 text-amber-950' : 'border-border bg-surface-muted text-ink' }`}
 >
 <div
 className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${ isFreeTierActive ? 'border-border bg-surface text-ink' : 'border-border bg-surface text-ink' }`}
 >
 {isFreeTierActive ? (
 <Sparkles className="h-3.5 w-3.5" />
 ) : (
 <Crown className="h-3.5 w-3.5" />
 )}
 </div>
 <p className="text-sm leading-relaxed">
 You are on <strong className="font-semibold">{currentPlan.name}</strong>
 {isFreeTierActive && canStillUpgrade ? (
 <>
 {' '}
 — premium pages stay locked until you upgrade. Pick a paid tier below for branding,
 analytics, and more.
 </>
 ) : canStillUpgrade ? (
 <> — choose a higher plan below to unlock more capacity.</>
 ) : (
 '.'
 )}
 </p>
 </div>
 )}

 {hasActiveSubscription === false && (
 <div className="flex items-start gap-3 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3.5 sm:px-5">
 <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
 <div>
 <h3 className="text-sm font-semibold text-amber-950">
 {hasUsedFreeTier ? 'Subscription required' : 'Activate your workspace'}
 </h3>
 <p className="mt-1 text-sm text-amber-900/80 leading-relaxed">
 {hasUsedFreeTier
 ? 'Your free plan has expired. Free is available only once — choose Basic, Professional, or Premium to continue.'
 : 'No subscription is on file yet — choose Free or a paid plan below. New accounts are usually assigned Free automatically; refresh or pick a plan if you still see this.'}
 </p>
 </div>
 </div>
 )}
 </div>

 {showGlobalBillingToggle && (
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 product-panel px-4 py-4 sm:px-5 shadow-sm">
 <div>
 <p className="text-sm font-semibold text-ink">Billing period</p>
 <p className="text-xs text-ink-tertiary mt-0.5">
 Applies to paid plans that offer both intervals
 </p>
 </div>
 <div
 role="tablist"
 aria-label="Billing period for paid plans"
 className="product-tabs inline-flex self-start sm:self-auto"
 >
 {[
 { value: 'monthly', label: 'Monthly' },
 { value: 'yearly', label: 'Yearly' },
 ].map(({ value, label }) => {
 const isActive = globalBilling === value;
 return (
 <button
 key={value}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setGlobalBilling(value)}
 className={`product-tab min-w-[6.5rem] ${isActive ? 'is-active' : ''}`}
 >

 {label}
 </button>
 );
 })}
 </div>
 </div>
 )}

 <div className={`grid ${gridColsClass} gap-5 lg:gap-6 mb-10`}>
 {visiblePlans.map((plan) => {
 const isPopular = plan.slug === popularPaidSlug && !planIsFree(plan);
 const isCurrent = currentPlan && plan.id === currentPlan.id;
 const billing = normalizeBillingChoice(plan, globalBilling);

 return (
 <div key={plan.id} className={planCardShell(plan)}>
 {isPopular && (
 <div className="absolute top-4 right-4 z-10">
 <span className="inline-flex items-center gap-1 rounded-full bg-ink text-white px-2.5 py-1 text-[11px] font-semibold">
 <Star className="h-3 w-3 fill-current" />
 Popular
 </span>
 </div>
 )}

 <div className="flex flex-1 flex-col p-5 sm:p-6">
 <div className="mb-4 pr-16">
 <span
 className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${ planIsFree(plan) ? 'bg-gray-900 text-white' : isPopular ? 'bg-surface-muted text-ink' : 'bg-gray-100 text-gray-700' }`}
 >
 {planIsFree(plan) ? <Sparkles className="h-3 w-3" /> : null}
 {planTierLabel(plan, popularPaidSlug)}
 </span>
 {isCurrent && (
 <span className="ml-2 inline-flex rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
 Current
 </span>
 )}
 </div>

 <h3 className="text-xl font-bold tracking-tight text-ink">
 {plan.name}
 </h3>
 <p className="mt-1.5 text-sm text-ink-secondary leading-relaxed min-h-[2.75rem]">
 {planTierSubtitle(plan)}
 </p>

 <div className="mt-5 mb-6">
 <div className="flex items-baseline gap-1.5">
 <span className="text-3xl sm:text-4xl font-bold tracking-tight text-ink tabular-nums">
 {planIsFree(plan)
 ? 'Free'
 : `$${priceForBillingChoice(plan, globalBilling).toFixed(2)}`}
 </span>
 {!planIsFree(plan) && (
 <span className="text-sm text-ink-tertiary">
 /{billing === 'yearly' ? 'year' : 'month'}
 </span>
 )}
 </div>
 {planIsFree(plan) ? (
 <p className="mt-1.5 text-xs text-ink-tertiary">
 Renews monthly · upgrade anytime
 </p>
 ) : planOffersBothIntervals(plan) ? (
 <p className="mt-1.5 text-xs text-ink-tertiary">
 {globalBilling === 'yearly'
 ? `Billed yearly · $${planMonthlyPrice(plan).toFixed(2)}/mo equivalent`
 : `Or $${planYearlyPrice(plan).toFixed(2)}/year`}
 </p>
 ) : null}
 </div>

 <button
 type="button"
 onClick={() => handleSelectPlan(plan)}
 disabled={
 subscribingToFree ||
 (currentPlan &&
 (plan.id === currentPlan.id || comparePlanTier(currentPlan, plan) < 0))
 }
 className={`mt-auto w-full py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${planButtonClass(plan)}`}
 >
 {subscribingToFree ? (
 <span className="inline-flex items-center justify-center gap-2">
 <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
 Subscribing…
 </span>
 ) : currentPlan && plan.id === currentPlan.id ? (
 'Current plan'
 ) : currentPlan && comparePlanTier(currentPlan, plan) < 0 ? (
 'Lower tier'
 ) : currentPlan && comparePlanTier(currentPlan, plan) > 0 ? (
 `Upgrade to ${plan.name}`
 ) : planIsFree(plan) ? (
 `Start on ${plan.name}`
 ) : (
 `Choose ${plan.name}`
 )}
 </button>
 </div>

 <div
 className={`border-t px-5 py-5 sm:px-6 ${ planIsFree(plan) ? 'border-border bg-surface' : 'border-border bg-surface-muted' }`}
 >
 <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary mb-3">
 {planIsFree(plan) ? 'Starter toolkit' : 'Capacity'}
 </h4>
 <div className="space-y-2.5 mb-5">
 {[
 {
 icon: Users,
 text: plan.max_bookings_per_month
 ? `Up to ${plan.max_bookings_per_month} bookings/month`
 : 'Unlimited bookings',
 },
 {
 icon: Image,
 text: plan.max_services
 ? `Up to ${plan.max_services} services`
 : 'Unlimited services',
 },
 {
 icon: Calendar,
 text: plan.max_portfolio_items
 ? `Up to ${plan.max_portfolio_items} portfolio items`
 : 'Unlimited portfolio items',
 },
 {
 icon: Package,
 text:
 plan.max_packages_per_service != null
 ? `Up to ${plan.max_packages_per_service} packages per service`
 : 'Unlimited packages per service',
 },
 ].map((row) => {
 const Icon = row.icon;
 return (
 <div key={row.text} className="flex items-center gap-2.5">
 <Icon className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
 <span className="text-sm text-ink-secondary">{row.text}</span>
 </div>
 );
 })}
 </div>

 <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary mb-3">
 {planIsFree(plan) ? 'Locked until you upgrade' : 'Premium capabilities'}
 </h4>
 <div className="space-y-2.5">
 {renderFeature('analytics', plan.feature_analytics, 'Advanced analytics')}
 {renderFeature('custom_branding', plan.feature_custom_branding, 'Custom branding')}
 {renderFeature('priority_support', plan.feature_priority_support, 'Priority support')}
 {renderFeature('advanced_booking', plan.feature_advanced_booking, 'Advanced booking')}
 {renderFeature('multi_location', plan.feature_multi_location, 'Multi-location')}
 </div>

 {Array.isArray(plan.features_list) && plan.features_list.length > 0 && (
 <div className="mt-5 pt-4 border-t border-border">
 <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary mb-2.5">
 Highlights
 </p>
 <ul className="space-y-2">
 {plan.features_list.slice(0, 5).map((line, idx) => (
 <li
 key={idx}
 className="flex gap-2 text-sm text-ink-secondary leading-snug"
 >
 <Check className="h-3.5 w-3.5 text-ink shrink-0 mt-0.5" />
 <span>{line}</span>
 </li>
 ))}
 </ul>
 </div>
 )}
 </div>
 </div>
 );
 })}
 </div>

 <p className="text-center text-xs text-ink-tertiary max-w-lg mx-auto">
 Upgrades take effect immediately after payment. Need help choosing? Contact support from your
 dashboard.
 </p>

 <PaymentModal
 isOpen={showPaymentModal}
 onClose={() => setShowPaymentModal(false)}
 selectedPlan={selectedPlan}
 billingInterval={
 selectedPlan ? normalizeBillingChoice(selectedPlan, globalBilling) : 'monthly'
 }
 checkoutAmount={
 selectedPlan ? priceForBillingChoice(selectedPlan, globalBilling) : 0
 }
 onPaymentSuccess={handlePaymentSuccess}
 onPaymentError={handlePaymentError}
 />
 </div>
 </div>
 );
};

export default VendorSubscription;
